import { FormatType } from '@/data/models/formatType';
import { IAlbum } from '../data/models/album';
import { AlbumState } from '../data/models/albumState';
import { ILabel } from '../data/models/label';
import { IModel, ResponseModel } from '../data/models/model';
import { IOptions } from '../data/models/option';
import { ValidationMetadata } from '../data/models/validation';
import { ISalesTeam } from '../data/models/salesTerritory';
import { BaseApiClient } from './BaseAPIClient';
import { ICmsAlbum } from '@/data/models/cms';

export class ProductApiClient extends BaseApiClient {
  constructor() {
    super(BaseApiClient.findBaseUrl());
  }
  async createProduct(apiBody: any): Promise<ResponseModel<IAlbum>> {
    const path = '/prs/api/v1/albums';
    return await this.post(path, apiBody);
  }

  async updateProduct(
    id: string,
    apiBody: any
  ): Promise<ResponseModel<IAlbum>> {
    const path = `/prs/api/v1/albums/${id}`;
    return await this.put(path, apiBody);
  }

  async updateProductState(
    id: string,
    state: AlbumState,
    notes?: string
  ): Promise<ResponseModel<IAlbum>> {
    const path = `/prs/api/v1/albums/${id}/state`;
    const apiBody = { RequestedState: state, Notes: notes };
    return await this.put<ResponseModel<IAlbum>>(path, apiBody);
  }

  async getLabel(queryParams: any): Promise<IOptions[]> {
    const path = '/prs/api/labels';
    const response: any = await this.get(path, queryParams);
    let options: IOptions[] = [];
    if (response) {
      options = response.data.map((item: ILabel) => {
        const parentName = item.parentOrgName
          ? `<<${item.parentOrgName}>>`
          : '';
        return { value: item.adminOrgId, label: `${item.name} ${parentName}` };
      });
    }
    return options;
  }

  async getLabelById(id: string): Promise<any> {
    const path = `/prs/api/labels/${id}`;
    return await this.get(path);
  }

  async getLabelDetailById(id: string): Promise<any> {
    const path = `/prs/api/labels/${id}/detail`;
    return await this.get(path);
  }

  async getAlbum(id: string): Promise<ResponseModel<IAlbum>> {
    const path = `/prs/api/v1/albums/${id}`;
    return await this.get(path);
  }
  async getAlbumList(queryParams: any, formatType?: FormatType): Promise<any> {
    const path = `/prs/api/v1/albums/getAlbums?formatType=${formatType}`;
    return await this.post(path, queryParams);
  }
  async deleteAlbum(id: string): Promise<any> {
    const path = `/prs/api/v1/albums/${id}`;
    return await this.delete(path);
  }
  async validateAlbum(
    apiBody: string,
    includeSubmitValidations = false,
    changeRequestId?: number | string | null
  ): Promise<any> {
    const queryParams = new URLSearchParams();
    if (includeSubmitValidations) {
      queryParams.set('includeSubmitValidations', 'true');
    }
    if (changeRequestId != null) {
      queryParams.set('changeRequestId', changeRequestId.toString());
    }

    const queryString = queryParams.toString();
    const path = `/prs/api/v1/albums/validateAlbum${
      queryString ? `?${queryString}` : ''
    }`;
    return await this.post<any>(path, apiBody);
  }
  async export(fetchModel: any, excludeTracks: boolean): Promise<any> {
    const path = `/prs/api/v1/albums/export`;
    return await this.post(path, fetchModel, {
      params: { type: 'excel', excludeTracks: excludeTracks },
      responseType: 'blob',
    });
  }
  async getAlbumValidationMetadata(
    id: string
  ): Promise<ResponseModel<ValidationMetadata>> {
    const path = `/prs/api/v1/albums/${id}/validation-metadata`;
    return await this.get(path);
  }
  async getAlbums(
    ids: Array<number | string>
  ): Promise<ResponseModel<IAlbum[]>> {
    const path = '/prs/api/v1/albums';
    return await this.get(path, { ids: ids.join(',') });
  }
  async replicateProduct(
    id: string,
    productType: string,
    targetSystem: 'CMS'
  ): Promise<ResponseModel<IModel>> {
    const path = `/prs/api/v1/albums/${id}/replicate`;
    return await this.post(path, { targetSystem, productType });
  }

  async getAllowedSalesTeams(id: string): Promise<ResponseModel<ISalesTeam[]>> {
    const path = `/prs/api/v1/albums/${id}/allowed-sales-teams`;
    return await this.get(path);
  }

  async getAllSalesTeams(): Promise<ResponseModel<ISalesTeam[]>> {
    const path = `/prs/api/v1/sales-teams`;
    return await this.get(path);
  }
  async getCmsAlbums(
    ids: Array<number | string>
  ): Promise<ResponseModel<ICmsAlbum[]>> {
    const path = '/prs/api/v1/albums/cms';
    return await this.get(path, { id: ids.join(',') });
  }
}

export const productApiClient = new ProductApiClient();
